import { fitImportedImage, imageImportBounds } from "./image-sizing";
import { requireImportTarget } from "./import-target";
import {
  AssetRecordType,
  createShapeId,
  type TLImageShape,
} from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import { uploadFile } from "./api";
import { imagePlacement } from "./sheet-operations";

export async function importAttachment(
  editor: Editor,
  room: string,
  file: File,
  signal: AbortSignal,
  cloud: boolean,
  placement = imagePlacement(editor),
  shapeId: import("@tldraw/tlschema").TLShapeId = createShapeId(),
) {
  const source = await uploadFile(room, file, signal, cloud);
  signal.throwIfAborted();
  const { documentPreview } = await import("./document-previews");
  const preview = await documentPreview(file, signal);
  const uploaded = await uploadFile(
    room,
    new File([preview.blob], `${file.name}-preview.png`, { type: "image/png" }),
    signal,
    cloud,
  );
  signal.throwIfAborted();
  if (editor.getIsReadonly()) throw new Error("Edit access was removed.");
  if (!uploaded.image) throw new Error("File preview dimensions unavailable.");
  const parent = requireImportTarget(editor, placement);
  if (editor.getShape(shapeId)) return shapeId;
  const id = AssetRecordType.createId();
  const { w, h } =
    preview.kind === "image"
      ? fitImportedImage(preview, imageImportBounds(editor, parent.parentId))
      : { w: 480, h: (480 * preview.h) / preview.w };
  editor.markHistoryStoppingPoint("Add document");
  editor.run(() => {
    editor.createAssets([
      AssetRecordType.create({
        id,
        type: "image",
        props: {
          name: file.name,
          src: uploaded.src,
          w: uploaded.image!.w,
          h: uploaded.image!.h,
          mimeType: "image/png",
          isAnimated: false,
        },
        meta: {
          imagePreviews: true,
          documentSource: source.src,
          documentName: file.name,
          documentPreview: preview.note,
          ...("model" in preview && preview.model
            ? {
                modelFormat: preview.model,
                modelSource: source.src,
                modelName: file.name,
              }
            : {}),
          ...("cadThumbnail" in preview ? { cadThumbnail: true } : {}),
          imageFile: preview.kind === "image",
          ...(preview.format ? { imageFormat: preview.format } : {}),
          ...(preview.animated ? { imageAnimation: "poster" } : {}),
        },
      }),
    ]);
    editor.createShape<TLImageShape>({
      opacity: 1,
      id: shapeId,
      type: "image",
      parentId: parent.parentId,
      x: parent.center.x - w / 2,
      y: parent.center.y - h / 2,
      props: { w, h, assetId: id, altText: file.name },
    });
  });
  editor.select(shapeId);
  return shapeId;
}
